import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { QrCode, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { useAuth, getDefaultRoute } from "../context/AuthContext";
import api from "../lib/api";
import { getAttendanceFeatureLabel } from "../lib/attendance";
import StatusBadge from "../components/StatusBadge";
import { extractTime, formatDateTime, getErrorMessage } from "../lib/formatters";


export default function ScanPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, featureFlags, loading: authLoading } = useAuth();
  const [scanState, setScanState] = useState({ loading: true, result: null, error: "" });
  const hasSubmittedRef = useRef(false);
  const successFeedbackRef = useRef(false);
  const [successCountdown, setSuccessCountdown] = useState(3);

  const handleScan = async () => {
    if (hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;
    setScanState({ loading: true, result: null, error: "" });
    try {
      const response = await api.post(`/attendance/qr-scan/${token}`);
      setScanState({ loading: false, result: response.data, error: "" });
      toast.success("QR attendance recorded successfully");
    } catch (error) {
      const message = getErrorMessage(error);
      setScanState({ loading: false, result: null, error: message });
      toast.error(message);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      hasSubmittedRef.current = false;
      setScanState({ loading: false, result: null, error: "" });
      return;
    }
    if (featureFlags && (featureFlags.kiosk_qr_attendance || featureFlags.employee_qr_scan_attendance)) {
      handleScan();
      return;
    }
    if (featureFlags && !featureFlags.kiosk_qr_attendance && !featureFlags.employee_qr_scan_attendance) {
      hasSubmittedRef.current = false;
      setScanState({ loading: false, result: null, error: "QR attendance is disabled for this tenant." });
    }
  }, [authLoading, token, featureFlags, user]);

  useEffect(() => {
    hasSubmittedRef.current = false;
    successFeedbackRef.current = false;
  }, [token, user?.id]);

  useEffect(() => {
    if (!scanState.result) {
      setSuccessCountdown(3);
      successFeedbackRef.current = false;
      return undefined;
    }
    if (!successFeedbackRef.current && typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate([140, 60, 180]);
      successFeedbackRef.current = true;
    }
    const destination = user?.role === "manager" ? "/employee/dashboard" : getDefaultRoute(user);
    const intervalId = window.setInterval(() => {
      setSuccessCountdown((current) => {
        if (current <= 1) {
          window.clearInterval(intervalId);
          navigate(destination);
          return 0;
        }
        return current - 1;
      });
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [navigate, scanState.result, user]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FAF8F3] px-4 py-8" data-testid="scan-auth-loading-state">
        <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-2xl items-center justify-center">
          <Card className="w-full rounded-[32px] border-slate-200/70 bg-white/90 shadow-[0_28px_90px_rgba(15,23,42,0.1)]" data-testid="scan-auth-loading-card">
            <CardHeader>
              <CardTitle className="text-3xl font-extrabold" data-testid="scan-auth-loading-title">Preparing secure scan</CardTitle>
              <CardDescription data-testid="scan-auth-loading-description">Tarka HR is verifying your session so the QR scan can continue safely.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-3xl border border-[#F4A300]/20 bg-[#F4A300]/10 p-5 text-sm font-semibold text-[#C27D00]" data-testid="scan-auth-loading-copy">
                Loading your employee access…
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAF8F3] px-4 py-8" data-testid="scan-login-gate-page">
        <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-2xl items-center justify-center">
          <Card className="w-full rounded-[32px] border-slate-200/70 bg-white/90 shadow-[0_28px_90px_rgba(15,23,42,0.1)]" data-testid="scan-login-gate-card">
            <CardHeader>
              <CardTitle className="text-3xl font-extrabold" data-testid="scan-login-gate-title">Sign in to complete the scan</CardTitle>
              <CardDescription data-testid="scan-login-gate-description">This Tarka HR QR is valid, but attendance can only be recorded after the employee signs in.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-5" data-testid="scan-login-gate-token-panel">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">Scan token</p>
                <p className="mt-2 break-all text-sm font-semibold text-slate-800">{token}</p>
              </div>
              <div className="flex flex-wrap gap-3" data-testid="scan-login-gate-actions">
                <Button type="button" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" onClick={() => navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`)} data-testid="scan-login-gate-login-button">
                  Sign in and continue
                </Button>
                <Button type="button" variant="outline" className="rounded-2xl" onClick={() => navigate("/login")} data-testid="scan-login-gate-cancel-button">
                  Go to login
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,_#faf8f3_0%,_#fffdf8_100%)] px-4 py-8" data-testid="scan-page-root">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-3xl items-center justify-center">
        <Card className="w-full rounded-[32px] border-slate-200/70 bg-white/90 shadow-[0_28px_90px_rgba(15,23,42,0.1)]" data-testid="scan-result-card">
          <CardHeader className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-3xl bg-[#F4A300]/12 text-[#C27D00]">
                  <QrCode className="h-7 w-7" />
                </div>
                <div>
                  <CardTitle className="text-3xl font-extrabold" data-testid="scan-page-title">QR attendance checkpoint</CardTitle>
                  <CardDescription data-testid="scan-page-description">Secure, session-based attendance marking with tenant validation and expiry checks.</CardDescription>
                </div>
              </div>
              <Button type="button" variant="outline" className="rounded-2xl" onClick={() => navigate(getDefaultRoute(user))} data-testid="scan-back-button">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-5" data-testid="scan-token-panel">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400" data-testid="scan-token-label">Session token</p>
              <p className="mt-2 break-all text-sm font-semibold text-slate-800" data-testid="scan-token-value">{token}</p>
            </div>

            {scanState.loading ? (
              <div className="rounded-3xl border border-[#F4A300]/20 bg-[#F4A300]/10 p-6 text-center text-[#C27D00]" data-testid="scan-loading-state">
                Validating session and recording attendance...
              </div>
            ) : null}

            {!scanState.loading && scanState.error ? (
              <div className="rounded-3xl border border-rose-100 bg-rose-50 p-6" data-testid="scan-error-state">
                <StatusBadge value="Absent" testId="scan-error-badge" />
                <p className="mt-4 text-lg font-bold text-rose-700" data-testid="scan-error-message">{scanState.error}</p>
                <Button type="button" className="mt-5 rounded-2xl bg-[#0D1B2A] text-white hover:bg-[#08131F]" onClick={() => {
                  hasSubmittedRef.current = false;
                  handleScan();
                }} data-testid="scan-retry-button">
                  Retry scan
                </Button>
              </div>
            ) : null}

            {!scanState.loading && scanState.result ? (
              <div className="grid gap-4 md:grid-cols-2" data-testid="scan-success-grid">
                <div className="rounded-3xl border border-emerald-100 bg-emerald-50 p-5" data-testid="scan-session-summary-card">
                <div className="mx-auto mb-4 flex h-20 w-20 animate-[pulse_1.6s_ease-in-out_infinite] items-center justify-center rounded-full bg-emerald-100 text-emerald-700 shadow-[0_0_0_10px_rgba(16,185,129,0.12)]" data-testid="scan-success-animation-wrap">
                  <CheckCircle2 className="h-10 w-10" />
                </div>
                  <StatusBadge value="Present" testId="scan-success-badge" />
                  <p className="mt-4 text-lg font-bold text-emerald-700" data-testid="scan-success-message">Attendance updated successfully</p>
                  <p className="mt-2 text-sm text-emerald-700/80" data-testid="scan-session-label">Session: {scanState.result.session.label}</p>
                  <p className="mt-2 text-sm text-emerald-700/80" data-testid="scan-session-expiry">Expires at: {formatDateTime(scanState.result.session.expires_at)}</p>
                  <p className="mt-2 text-sm text-emerald-700/80" data-testid="scan-assignment-label">Method: {getAttendanceFeatureLabel(scanState.result.assigned_attendance_feature)}</p>
                  <p className="mt-3 text-sm font-semibold text-emerald-800" data-testid="scan-success-redirect-copy">Returning automatically in {successCountdown} second{successCountdown === 1 ? "" : "s"}…</p>
                <p className="mt-2 text-xs font-medium uppercase tracking-[0.22em] text-emerald-700/70" data-testid="scan-success-feedback-copy">Phone vibration and visual confirmation triggered</p>
                </div>
                <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="scan-record-summary-card">
                  <p className="text-xs font-semibold uppercase tracking-[0.26em] text-slate-400" data-testid="scan-record-title">Attendance result</p>
                  <p className="mt-3 text-sm text-slate-600" data-testid="scan-record-status">Status: {scanState.result.record.status}</p>
                  <p className="mt-2 text-sm text-slate-600" data-testid="scan-record-clock-in">Clock in: {extractTime(scanState.result.record.clock_in)}</p>
                  <p className="mt-2 text-sm text-slate-600" data-testid="scan-record-clock-out">Clock out: {extractTime(scanState.result.record.clock_out)}</p>
                  <p className="mt-2 text-sm text-slate-600" data-testid="scan-record-source">Source: {scanState.result.record.source}</p>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}